/**
 * L'ÉDITEUR DE FIGURE DE LA CONSOLE — atelier de figures, lot L7 (AB#943 ;
 * `docs/pilotage/atelier-figures/CONCEPTION.md` § 5.3). Ce que l'écran
 * DÉCIDE vit ici, pour être éprouvé sans navigateur.
 *
 * L'auteur construit sur des COUCHES : la figure de l'énoncé, la solution,
 * chaque tracé fautif. Chaque couche est un bac à sable de la relecture
 * (`relecture-trace.ts`, L0) — le même aimant, le même moteur, les mêmes
 * gestes que l'atelier de l'élève (un geste, une étape ; Annuler défait la
 * dernière) —, augmenté des outils de CONSTRUCTION que l'auteur emploie le
 * plus : milieu, médiatrice, perpendiculaire, parallèle, bissectrice,
 * intersection. Tout le reste (les contraintes proposées, les griefs, la
 * sortie) est calculé par le paquet partagé (`editeur-figure.ts`), le même
 * qu'éprouve le test de l'import.
 *
 * Aucun appel au modèle (I-11), aucune écriture en base : la sortie est le
 * JSON d'un exercice, copié ou téléchargé, que la chaîne d'import fait entrer.
 */
import {
  aimanter,
  exerciceDuProjet,
  planDuProjet,
  resoudreConstruction,
  type CibleEditee,
  type ContrainteRetenue,
  type ExerciceEdite,
  type FautifEdite,
  type FigureV2Plan,
  type IdObjet,
  type ObjetConstructionDto,
  type PointV2,
  type ProjetDeFigure,
  type RepereV2Dto,
} from '@silex/shared';

import {
  documentDuTrace,
  idLibre,
  nomLibre,
  toucherLeBac,
  type BacASable,
  type OutilAuClic,
} from './relecture-trace';

/* ─────────────────────────── les outils de l'auteur ─────────────────────────── */

/** Les outils de construction que l'auteur emploie au clic, en plus de ceux du bac. */
export const OUTILS_DE_CONSTRUCTION = [
  'milieu',
  'mediatrice',
  'perpendiculaire',
  'parallele',
  'bissectrice',
  'intersection',
] as const;
export type OutilDeConstruction = (typeof OUTILS_DE_CONSTRUCTION)[number];
export type OutilDeLAuteur = OutilAuClic | OutilDeConstruction;

export const NOM_OUTIL_AUTEUR: Readonly<Record<OutilDeConstruction, string>> = {
  milieu: 'Milieu',
  mediatrice: 'Médiatrice',
  perpendiculaire: 'Perpendiculaire',
  parallele: 'Parallèle',
  bissectrice: 'Bissectrice',
  intersection: 'Intersection',
};

/** Ce que chaque outil attend, dans l'ordre : dit à l'auteur sous la figure. */
export const AIDE_OUTIL: Readonly<Record<OutilDeLAuteur, string>> = {
  point: 'Touche la figure pour poser un point.',
  segment: 'Touche deux points.',
  droite: 'Touche deux points.',
  vecteur: 'Touche son origine, puis son extrémité.',
  cercle: 'Touche le centre, puis un point du cercle.',
  polygone: 'Touche les sommets, puis le premier pour fermer.',
  angle: 'Touche un point, le sommet, puis l’autre point.',
  milieu: 'Touche les deux extrémités.',
  mediatrice: 'Touche les deux extrémités du segment.',
  perpendiculaire: 'Touche une droite, puis le point par où elle passe.',
  parallele: 'Touche une droite, puis le point par où elle passe.',
  bissectrice: 'Touche un point, le sommet, puis l’autre point.',
  intersection: 'Touche deux lignes ou cercles.',
};

type Plan = Pick<{ repere: RepereV2Dto; enonce: FigureV2Plan | null }, 'repere' | 'enonce'>;

/** Le point sous le doigt, ou un point neuf : son identifiant, et les objets. */
function pointSous(
  t: Plan,
  objets: readonly ObjetConstructionDto[],
  p: PointV2,
  rayon: number,
): { readonly id: IdObjet; readonly objets: readonly ObjetConstructionDto[] } {
  const resolue = resoudreConstruction(documentDuTrace(t, objets), t.enonce);
  const cible = aimanter(p, resolue, rayon, { grille: resolue.repere.pas });
  if (cible.genre === 'point') return { id: cible.id, objets };
  const pris = new Set<string>();
  for (const o of resolue.objets.values()) if (o.nom !== undefined) pris.add(o.nom);
  const id = idLibre(objets, 'p');
  const arrondi = (v: number): number => Math.round(v * 1e4) / 1e4;
  return {
    id,
    objets: [
      ...objets,
      { id, nom: nomLibre(pris), def: { type: 'point_libre', x: arrondi(cible.p.x), y: arrondi(cible.p.y) } },
    ],
  };
}

/** La ligne ou le cercle sous le doigt ; `null` : rien de tel. */
function traitSous(
  t: Plan,
  objets: readonly ObjetConstructionDto[],
  p: PointV2,
  rayon: number,
  genres: readonly string[],
): IdObjet | null {
  const resolue = resoudreConstruction(documentDuTrace(t, objets), t.enonce);
  const cible = aimanter(p, resolue, rayon, { grille: null });
  if (cible.genre !== 'objet') return null;
  const r = resolue.objets.get(cible.id);
  return r !== undefined && genres.includes(r.genre) ? cible.id : null;
}

/**
 * Un toucher de l'auteur. Les outils du bac passent par le bac (`toucherLeBac`) ;
 * ceux de construction posent leur définition quand ils ont reçu ce qu'ils
 * attendent. Un geste commencé garde l'état d'avant pour Annuler.
 */
export function toucherEnAuteur(
  t: Plan,
  bac: BacASable,
  outil: OutilDeLAuteur,
  p: PointV2,
  rayon: number,
): BacASable {
  if (!(OUTILS_DE_CONSTRUCTION as readonly string[]).includes(outil)) {
    return toucherLeBac(t, bac, outil as OutilAuClic, p, rayon);
  }
  const passe = bac.enCours.length === 0 ? [...bac.passe, bac.objets] : bac.passe;
  const poser = (objets: readonly ObjetConstructionDto[], prefixe: string, def: ObjetConstructionDto['def']): BacASable => ({
    objets: [...objets, { id: idLibre(objets, prefixe), def }],
    enCours: [],
    passe,
  });
  switch (outil as OutilDeConstruction) {
    case 'milieu':
    case 'mediatrice': {
      const { id, objets } = pointSous(t, bac.objets, p, rayon);
      const enCours = [...bac.enCours, id];
      if (enCours.length < 2) return { objets, enCours, passe };
      const [de, a] = enCours as [IdObjet, IdObjet];
      return outil === 'milieu'
        ? poser(objets, 'p', { type: 'milieu', de, a })
        : poser(objets, 'd', { type: 'mediatrice', de, a });
    }
    case 'bissectrice': {
      const { id, objets } = pointSous(t, bac.objets, p, rayon);
      const enCours = [...bac.enCours, id];
      if (enCours.length < 3) return { objets, enCours, passe };
      const [de, sommet, a] = enCours as [IdObjet, IdObjet, IdObjet];
      return poser(objets, 'd', { type: 'bissectrice', sommet, de, a });
    }
    case 'perpendiculaire':
    case 'parallele': {
      if (bac.enCours.length === 0) {
        const ligne = traitSous(t, bac.objets, p, rayon, ['ligne']);
        return ligne === null ? bac : { objets: bac.objets, enCours: [ligne], passe };
      }
      const { id, objets } = pointSous(t, bac.objets, p, rayon);
      const a = bac.enCours[0] as IdObjet;
      return poser(
        objets,
        'd',
        outil === 'perpendiculaire' ? { type: 'perpendiculaire', a, par: id } : { type: 'parallele', a, par: id },
      );
    }
    case 'intersection': {
      const trait = traitSous(t, bac.objets, p, rayon, ['ligne', 'cercle']);
      if (trait === null || bac.enCours.includes(trait)) return bac;
      const enCours = [...bac.enCours, trait];
      if (enCours.length < 2) return { objets: bac.objets, enCours, passe };
      const [de, a] = enCours as [IdObjet, IdObjet];
      return poser(bac.objets, 'p', { type: 'intersection', de, a, branche: 0 });
    }
  }
}

/** Renomme un objet de la couche (un point de la figure, surtout) ; vide : sans nom. */
export function renommer(bac: BacASable, id: IdObjet, nom: string): BacASable {
  const propre = nom.trim();
  return {
    ...bac,
    passe: [...bac.passe, bac.objets],
    objets: bac.objets.map((o) => {
      if (o.id !== id) return o;
      const { nom: _ancien, ...reste } = o;
      return propre === '' ? reste : { ...reste, nom: propre };
    }),
  };
}

/** L'autre branche d'une intersection (deux solutions : droite ∩ cercle, cercle ∩ cercle). */
export function changerDeBranche(bac: BacASable, id: IdObjet): BacASable {
  return {
    ...bac,
    passe: [...bac.passe, bac.objets],
    objets: bac.objets.map((o) =>
      o.id === id && o.def.type === 'intersection'
        ? { ...o, def: { ...o.def, branche: o.def.branche === 0 ? 1 : 0 } }
        : o,
    ),
  };
}

/* ─────────────────────────── le projet de la console ─────────────────────────── */

/** La couche que l'auteur construit. */
export type Couche = 'figure' | 'solution' | { readonly fautif: number };

/** Ce que l'écran tient, en plus des couches : les choix de l'auteur. */
export interface EtatEditeur {
  readonly champs: Omit<
    ProjetDeFigure,
    'figure' | 'solution' | 'cibles' | 'contraintes' | 'fautifs'
  >;
  readonly figure: BacASable;
  readonly solution: BacASable;
  readonly cibles: readonly CibleEditee[];
  /** Les contraintes cochées, par leur clé. */
  readonly contraintes: Readonly<Record<string, ContrainteRetenue>>;
  readonly fautifs: readonly (Omit<FautifEdite, 'objets' | 'contraintes'> & {
    readonly bac: BacASable;
    readonly contraintes: Readonly<Record<string, ContrainteRetenue['contrainte']>>;
  })[];
}

export const REPERE_PAR_DEFAUT: RepereV2Dto = {
  xMin: -5,
  xMax: 5,
  yMin: -5,
  yMax: 5,
  axes: true,
  grille: true,
  pas: 1,
};

const BAC_NEUF: BacASable = { objets: [], enCours: [], passe: [] };

export const ETAT_INITIAL: EtatEditeur = {
  champs: {
    chapitre: '',
    alias: '',
    notion: 'N1',
    niveauExercice: 1,
    competence: 'MAT-C2',
    categorie: 'tracer',
    minutesEstimees: 5,
    enonce: '',
    decrit: '',
    repere: REPERE_PAR_DEFAUT,
    nature: 'trace',
    demarcheReference: [
      { ecriture: '', justification: '' },
      { ecriture: '', justification: '' },
    ],
    indices: [],
    outils: ['point', 'droite'],
    controle: '',
    resultatAttendu: '',
    resultatControle: '',
  },
  figure: BAC_NEUF,
  solution: BAC_NEUF,
  cibles: [],
  contraintes: {},
  fautifs: [],
};

/** Le projet, au contrat du paquet partagé. */
export function projetDeLEtat(e: EtatEditeur): ProjetDeFigure {
  const ids = new Set(e.solution.objets.map((o) => o.id));
  return {
    ...e.champs,
    figure: e.figure.objets,
    solution: e.solution.objets,
    cibles: e.cibles.filter((c) => ids.has(c.objet)),
    contraintes: Object.values(e.contraintes),
    fautifs: e.fautifs.map(({ bac, contraintes, ...f }) => ({
      ...f,
      objets: bac.objets,
      contraintes: Object.values(contraintes),
    })),
  };
}

/** La figure de l'énoncé normalisée, et le plan où l'on trace la solution et les fautifs. */
export function planDeLEtat(e: EtatEditeur): {
  readonly enonce: FigureV2Plan | null;
  readonly repere: RepereV2Dto;
} {
  const enonce = planDuProjet({ repere: e.champs.repere, figure: e.figure.objets });
  return { enonce, repere: e.champs.repere };
}

/** L'exercice et ses griefs, en direct. */
export function sortieDeLEtat(e: EtatEditeur): ExerciceEdite {
  return exerciceDuProjet(projetDeLEtat(e));
}

/** Le bac de la couche. */
export function bacDe(e: EtatEditeur, c: Couche): BacASable {
  if (c === 'figure') return e.figure;
  if (c === 'solution') return e.solution;
  return e.fautifs[c.fautif]?.bac ?? BAC_NEUF;
}

/** Remplace le bac de la couche. */
export function avecBac(e: EtatEditeur, c: Couche, bac: BacASable): EtatEditeur {
  if (c === 'figure') return { ...e, figure: bac };
  if (c === 'solution') return { ...e, solution: bac };
  return {
    ...e,
    fautifs: e.fautifs.map((f, i) => (i === c.fautif ? { ...f, bac } : f)),
  };
}

/** Un tracé fautif neuf : il part de la solution, que l'auteur déforme. */
export function ajouterFautif(e: EtatEditeur): EtatEditeur {
  return {
    ...e,
    fautifs: [
      ...e.fautifs,
      {
        reponse: '',
        erreur: '',
        cheminement: '',
        controle: '',
        bac: { objets: e.solution.objets, enCours: [], passe: [] },
        contraintes: {},
      },
    ],
  };
}

/** Le projet se garde dans le navigateur de l'auteur (un brouillon, jamais une écriture en base). */
export const CLE_BROUILLON = 'silex.editeur-figure.v1';

export function lireBrouillon(brut: string | null): EtatEditeur | null {
  if (brut === null) return null;
  try {
    const e = JSON.parse(brut) as EtatEditeur;
    return e !== null && typeof e === 'object' && 'champs' in e && 'figure' in e ? e : null;
  } catch {
    return null;
  }
}
